import styles from "./AgentsPanel.module.css";
import { DEFAULT_EDGE_BOX_KIND_INDEX, EDGE_BOX_KINDS, kindAt } from "./edgeBoxKinds";

export type HardwareValue = { hardware_model: string; allowed_number_of_cameras: number };

export type HardwareState = {
  index: number;
  customModel: string;
  customCapacity: string;
};

export const emptyHardwareState = (index: number = DEFAULT_EDGE_BOX_KIND_INDEX): HardwareState => ({
  index,
  customModel: "",
  customCapacity: "",
});

export const customHardwareState = (model: string, capacity: number): HardwareState => ({
  index: EDGE_BOX_KINDS.findIndex((k) => k.custom),
  customModel: model,
  customCapacity: String(capacity),
});

/** Resolve the form state to a submittable value, or null when a Custom entry is incomplete. */
export const resolveHardware = (state: HardwareState): HardwareValue | null => {
  const kind = kindAt(state.index);
  if (kind.custom) {
    const cap = Number(state.customCapacity);
    if (state.customModel.trim() === "" || !Number.isInteger(cap) || cap <= 0) return null;
    return { hardware_model: state.customModel.trim(), allowed_number_of_cameras: cap };
  }
  return { hardware_model: kind.name, allowed_number_of_cameras: kind.capacity ?? 0 };
};

type Props = {
  state: HardwareState;
  onChange: (next: HardwareState) => void;
  busy?: boolean;
};

/** Controlled edge-box picker fields: hardware dropdown + description + camera capacity.
 *  A fixed kind shows read-only description + capacity; "Custom" makes both editable. */
export const HardwareFields = ({ state, onChange, busy }: Props) => {
  const kind = kindAt(state.index);
  return (
    <>
      <label className={styles.field}>
        <span>Edge box hardware</span>
        <select
          aria-label="Edge box hardware"
          value={state.index}
          onChange={(e) => onChange({ ...state, index: Number(e.target.value) })}
          disabled={busy}
        >
          {EDGE_BOX_KINDS.map((k, i) => (
            <option key={k.name} value={i}>
              {k.name}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.field}>
        <span>Hardware description</span>
        {kind.custom ? (
          <input
            aria-label="Hardware description"
            value={state.customModel}
            onChange={(e) => onChange({ ...state, customModel: e.target.value })}
            placeholder="e.g. Dell OptiPlex micro, Ubuntu 24.04"
            disabled={busy}
          />
        ) : (
          <input aria-label="Hardware description" value={kind.description} readOnly disabled />
        )}
      </div>

      <div className={styles.field}>
        <span>Max number of cameras allowed</span>
        {kind.custom ? (
          <input
            aria-label="Max number of cameras allowed"
            type="number"
            min={1}
            value={state.customCapacity}
            onChange={(e) => onChange({ ...state, customCapacity: e.target.value })}
            disabled={busy}
          />
        ) : (
          <input
            aria-label="Max number of cameras allowed"
            value={String(kind.capacity ?? "")}
            readOnly
            disabled
          />
        )}
      </div>
    </>
  );
};
